import { useMemo } from 'react'
import BigNumber from 'bignumber.js'
import { orderBy } from 'lodash'
import { useAccount } from 'wagmi'
import { useAllSpotSwapTokens } from '~/state/dex/hooks'
import { useNativeTokenBalance, useTokenBalances } from '~/state/wallet/hooks'
import { useTokensPrice } from '~/hooks/useTokenPrice'
import { OrderedSpotSwapToken } from '~/components/Exchange/components/TokenListModal/types'
import { zeroAddress } from 'viem'
import { getAddress } from '@ethersproject/address'

export const useAllSortedTokens = () => {
  const { address: account, chainId: currentChainId } = useAccount()

  const allTokens = useAllSpotSwapTokens()
  const currentNetworkTokens = allTokens.filter(
    (token) => token.chainId === currentChainId,
  )
  const currentNativeTokenBalance =
    useNativeTokenBalance()?.toDecimalsFixed() ?? '0'
  const currentTokensBalances = useTokenBalances(currentNetworkTokens)
  const currentTokensPrices = useTokensPrice(
    currentNetworkTokens.map((token) => token.address),
    'USD',
    currentChainId,
  )

  return useMemo((): OrderedSpotSwapToken[] => {
    const initSortedTokens = orderBy(
      allTokens,
      ['chainId', (item) => item.symbol.toLowerCase()],
      ['asc', 'asc'],
    )

    if (account) {
      const resetTokens = initSortedTokens.filter(
        (token) => token.chainId !== currentChainId,
      )

      const nativeTokenPriceUSD = new BigNumber(
        currentTokensPrices[zeroAddress],
      )
        .multipliedBy(currentNativeTokenBalance)
        .toFixed(2)
      const nativeToken = {
        ...currentNetworkTokens.find((token) => token.address === zeroAddress),
        balance: currentNativeTokenBalance,
        price: currentTokensPrices[zeroAddress],
        priceUSD: nativeTokenPriceUSD,
      }
      const filledCurrentNetworkTokens = currentNetworkTokens.map((token) => {
        const address = getAddress(token.address)
        if (address === zeroAddress) {
          return nativeToken
        }

        const balance =
          currentTokensBalances[token.address]?.toDecimalsFixed() ?? '0'
        const price = currentTokensPrices[address]
        const priceUSD = new BigNumber(price).multipliedBy(balance).toFixed(2)

        return {
          ...token,
          balance,
          price,
          priceUSD,
        }
      }) as OrderedSpotSwapToken[]
      const sortedCurrentNetworkTokens = orderBy(
        filledCurrentNetworkTokens,
        [
          (item) => Number(item.priceUSD),
          (item) => Number(item.balance),
          'symbol',
        ],
        ['desc', 'desc', 'asc'],
      )
      return sortedCurrentNetworkTokens.concat(resetTokens)
    }
    return initSortedTokens
  }, [
    account,
    allTokens,
    currentChainId,
    currentNetworkTokens,
    currentNativeTokenBalance,
    currentTokensBalances,
    currentTokensPrices,
  ])
}
